import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { Badge } from "@/shared/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/shared/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/shared/components/ui/table";
import { DataPagination } from "@/shared/components/DataPagination";
import { usePagination } from "@/shared/hooks/usePagination";
import { resolveApiErrorMessage } from "@/shared/utils/apiErrorMessage";
import { IntegrationBaseService } from "../services/integrationsBaseService";
import type { IntegrationBase, IntegrationUsageItem } from "../types/integration";

interface IntegrationUsageTabProps {
  integration: IntegrationBase;
}

const USAGE_LIMIT = 50;
const PAGE_SIZE = 10;

/**
 * Aba Uso (AB#87542): quantas vezes cada tool foi chamada e as chamadas recentes. A contagem
 * é das últimas `USAGE_LIMIT` chamadas que a api devolve, não de todo o histórico.
 */
export const IntegrationUsageTab = ({ integration }: IntegrationUsageTabProps) => {
  const { t } = useTranslation();
  const { data, isLoading, error } = useQuery({
    queryKey: ["integration-usage", integration.id],
    queryFn: () => IntegrationBaseService.getUsage(integration.id, USAGE_LIMIT),
  });

  const { currentPage, pageSize, setCurrentPage, setPageSize, paginateData } = usePagination<IntegrationUsageItem>({
    initialPageSize: PAGE_SIZE,
  });

  const counts = useMemo(
    () => Object.entries(data?.counts ?? {}).sort((a, b) => b[1] - a[1]),
    [data],
  );
  const items = data?.items ?? [];

  if (isLoading) {
    return (
      <div className="flex min-h-[160px] items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <p className="text-sm text-destructive">
        {resolveApiErrorMessage(error, t)}
      </p>
    );
  }

  if (items.length === 0) {
    return <p className="py-8 text-center text-sm text-muted-foreground">{t("integrations.usage.empty")}</p>;
  }

  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t("integrations.usage.byTool")}</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2">
            {counts.map(([name, count]) => (
              <li key={name} className="flex items-center justify-between gap-2">
                <span className="truncate font-mono text-xs">{name}</span>
                <Badge variant="secondary">{count}</Badge>
              </li>
            ))}
          </ul>
          <p className="mt-3 text-[11px] text-muted-foreground">{t("integrations.usage.window", { count: USAGE_LIMIT })}</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t("integrations.usage.recent")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("integrations.usage.columns.tool")}</TableHead>
                <TableHead>{t("integrations.usage.columns.when")}</TableHead>
                <TableHead>{t("integrations.usage.columns.status")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginateData(items).map((item, index) => (
                <TableRow key={`${item.timestamp}-${item.toolName}-${index}`}>
                  <TableCell className="font-mono text-xs">{item.toolName}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {new Date(item.timestamp).toLocaleString()}
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={item.status === "success" ? "success" : item.status === "error" ? "destructive" : "outline"}
                      className="text-[10px]"
                    >
                      {t(`integrations.usage.status.${item.status}`, item.status)}
                    </Badge>
                    {item.errorMessage && (
                      <p className="mt-1 line-clamp-2 text-[11px] text-destructive">{item.errorMessage}</p>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {items.length >= PAGE_SIZE && (
            <DataPagination
              currentPage={currentPage}
              pageSize={pageSize}
              totalItems={items.length}
              onPageChange={setCurrentPage}
              onPageSizeChange={setPageSize}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
};
